// Media helpers: browser-side photo compression + chunked uploads for large videos.
// Photos are downscaled/re-encoded in the browser (Safari decodes HEIC natively, so
// iPhone photos also become universally viewable JPEGs). Videos beyond the single
// request cap are split into ~22 MB parts and reassembled server-side - original
// quality preserved, works on every browser including iPhone Safari.
import { api } from "@/lib/api";

export const REGULAR_MAX_BYTES = 25 * 1024 * 1024;   // single-request cap (ingress limit)
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;    // chunked-upload cap for videos
const CHUNKED_THRESHOLD = 24 * 1024 * 1024;          // use chunked path above this
const IMG_COMPRESS_THRESHOLD = 3 * 1024 * 1024;      // compress photos bigger than 3 MB
const IMG_MAX_DIMENSION = 2200;                       // plenty for support/gallery review
const HEIC_RE = /\.(heic|heif)$/i;

export const isVideo = (file) =>
  (file.type || "").startsWith("video/") || /\.(mov|mp4|m4v|webm|3gp|avi)$/i.test(file.name || "");

export const isImage = (file) =>
  (file.type || "").startsWith("image/") || HEIC_RE.test(file.name || "");

export const fileSizeLabel = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const decodeToBitmap = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });

/** Compress a photo in the browser when it's large. Returns the original file when
 *  compression isn't possible (e.g. HEIC on a non-Safari browser) or not worth it. */
export const compressImageIfNeeded = async (file) => {
  if (!isImage(file) || file.size <= IMG_COMPRESS_THRESHOLD) return file;
  try {
    const img = await decodeToBitmap(file);
    const scale = Math.min(1, IMG_MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (blob && blob.size < file.size) {
      const newName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      return new File([blob], newName, { type: "image/jpeg" });
    }
  } catch (e) {
    console.debug(`Image compression skipped for ${file.name}:`, e?.message);
  }
  return file; // fall back to the original - server accepts it as-is
};

/** Validate a file against upload limits. Returns an error string or null when OK. */
export const uploadLimitError = (file) => {
  if (isVideo(file)) {
    if (file.size > VIDEO_MAX_BYTES) {
      return `"${file.name}" is ${fileSizeLabel(file.size)} — videos can be up to 200 MB. Try trimming the clip.`;
    }
    return null;
  }
  if (file.size > REGULAR_MAX_BYTES) {
    return `"${file.name}" is ${fileSizeLabel(file.size)} — the max is 25 MB for this file type.`;
  }
  return null;
};

const uploadChunked = async (file, { petId, onProgress } = {}) => {
  const { data: init } = await api.post("/uploads/chunked/init", {
    filename: file.name, contentType: file.type || null, size: file.size, petId: petId || null,
  });
  const partMax = init.partMaxBytes;
  const totalParts = Math.ceil(file.size / partMax);
  for (let i = 0; i < totalParts; i++) {
    const fd = new FormData();
    fd.append("file", file.slice(i * partMax, (i + 1) * partMax));
    await api.post(`/uploads/chunked/${init.uploadId}/part?index=${i}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress?.(Math.round(((i + 1) / (totalParts + 1)) * 100));
  }
  const { data } = await api.post(`/uploads/chunked/${init.uploadId}/complete`);
  onProgress?.(100);
  return data;
};

/** Upload any file the smart way: photos get compressed first; large videos go through
 *  the chunked path automatically. Returns {id, filename, contentType, size}. */
export const uploadFileSmart = async (file, { petId, silent = true, onProgress } = {}) => {
  const prepared = await compressImageIfNeeded(file);
  if (prepared.size > CHUNKED_THRESHOLD) {
    return uploadChunked(prepared, { petId, onProgress });
  }
  const fd = new FormData();
  fd.append("file", prepared);
  const qs = new URLSearchParams();
  if (silent) qs.set("silent", "true");
  if (petId) qs.set("petId", petId);
  const { data } = await api.post(`/uploads?${qs.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  onProgress?.(100);
  return data;
};
